<template>
  <v-app>
    <v-main>
      <form>
        <v-container
          v-if="gtoric_calc_allowed === false"
          grid-list-md
          class="ghost"
        >
          <v-row class="justify-center">
            <v-col cols="11" sm="8">
              <v-sheet elevation="1" class="mt-14 pa-2 pa-sm-4">
                <p class="text-center text-h6 text-sm-h5">
                  To perform a toric calculation :<br />
                  Please first complete a spherical IOL calculation<br />
                  Then select an "IOL Power" value<br />
                  Then proceed to toric calculation.<br />
                </p>
              </v-sheet>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-else grid-list-md class="ghost">
          <iol-result-header :fields="results_header" />
          <v-sheet :class="side ? 'iol-right-v-sheet' : 'iol-left-v-sheet'">
            <h2 :align="side ? 'left' : 'right'">
              {{ side ? 'OD' : 'OS' }}
            </h2>
            <v-row>
              <v-col class="col-12 col-md-6" :order-sm="side ? '1' : '2'">
                <iol-result-used-param :fields="common_inputs" />
                <v-col v-if="glastCalc === 'second'">
                  <iol-result-used-param :fields="common_inputs_cont" />
                </v-col>
                <v-col v-if="['complex', 'second'].includes(glastCalc)">
                  <iol-result-corn-ref-surg :fields="corneal_history" />
                  <iol-result-switch-history :fields="switch_hist" />
                </v-col>
                <iol-selected-power-refract :fields="selected_power_refract" />
              </v-col>
              <v-col class="col-12 col-md-6" :order-sm="side ? '2' : '1'">
                <v-row>
                  <v-col class="col-12 col-md-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model.number="inputs.k1_axis"
                          label="K1 axis (°)"
                          outlined
                          class="iol-v-text-field"
                          type="number"
                          step="1"
                          ref="first"
                          required
                          v-on="on"
                          :error-messages="
                            getErrors('k1_axis', $v.inputs.k1_axis)
                          "
                          @input="$v.inputs.k1_axis.$touch()"
                          @blur="$v.inputs.k1_axis.$touch()"
                        />
                      </template>
                      <span
                        >Flat axis of anterior corneal surface, in
                        degrees.</span
                      >
                    </v-tooltip>
                    <v-text-field
                      v-model.number="inputs.sia"
                      label="SIA (D)"
                      outlined
                      class="iol-v-text-field"
                      type="number"
                      step="0.1"
                      required
                      :error-messages="getErrors('sia', $v.inputs.sia)"
                      @input="$v.inputs.sia.$touch()"
                      @blur="$v.inputs.sia.$touch()"
                    />
                    <v-text-field
                      v-model.number="inputs.sia_axis"
                      label="Incision axis (°)"
                      outlined
                      class="iol-v-text-field"
                      type="number"
                      step="1"
                      required
                      :error-messages="
                        getErrors('sia_axis', $v.inputs.sia_axis)
                      "
                      @input="$v.inputs.sia_axis.$touch()"
                      @blur="$v.inputs.sia_axis.$touch()"
                    />
                    <v-select
                      label="Toric IOL Style"
                      outlined
                      class="smooth iol-v-select"
                      v-model="inputs.style"
                      :items="$parent.style"
                      return-object
                      required
                    />
                    <v-select
                      label="Residual cylinder notation"
                      outlined
                      class="smooth iol-v-select"
                      v-model="inputs.notation"
                      :items="$parent.notation"
                      return-object
                      required
                    />
                  </v-col>
                  <v-col class="col-12 col-md-8">
                    <v-sheet class="iol-opt-v-sheet">
                      <h3>
                        Optional :<br />
                        <span v-bind:style="{ color: pr12pk12cylColor }">
                          (PR1/PR2 or PK1/PK2 or Post. Cylinder)</span
                        >
                      </h3>
                      <br />
                      <v-row class="justify-space-between">
                        <v-col class="col-12 col-md-4">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-model.number="inputs.pr1"
                                label="PR1 (mm)"
                                outlined
                                class="iol-v-text-field"
                                type="number"
                                step="1"
                                v-on="on"
                                :error-messages="
                                  getErrors('pr1', $v.inputs.pr1)
                                "
                                @input="$v.inputs.pr1.$touch()"
                                @blur="$v.inputs.pr1.$touch()"
                              />
                            </template>
                            <span
                              >PR1 from topographer or IOLMaster700 can be used
                              here.</span
                            >
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-model.number="inputs.pr2"
                                label="PR2 (mm)"
                                outlined
                                class="iol-v-text-field"
                                type="number"
                                step="1"
                                v-on="on"
                                :error-messages="
                                  getErrors('pr2', $v.inputs.pr2)
                                "
                                @input="$v.inputs.pr2.$touch()"
                                @blur="$v.inputs.pr2.$touch()"
                              />
                            </template>
                            <span
                              >PR2 from topographer or IOLMaster700 can be used
                              here.</span
                            >
                          </v-tooltip>
                        </v-col>
                        <v-col class="col-12 col-md-4">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-model.number="inputs.pk1"
                                label="PK1 (D)"
                                outlined
                                class="iol-v-text-field"
                                type="number"
                                step="0.1"
                                v-on="on"
                                :error-messages="
                                  getErrors('pk1', $v.inputs.pk1)
                                "
                                @input="$v.inputs.pk1.$touch()"
                                @blur="$v.inputs.pk1.$touch()"
                              />
                            </template>
                            <span
                              >PK1 from topographer or IOLMaster700 can be used
                              here.</span
                            >
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-model.number="inputs.pk2"
                                label="PK2 (D)"
                                outlined
                                class="iol-v-text-field"
                                type="number"
                                step="0.1"
                                v-on="on"
                                :error-messages="
                                  getErrors('pk2', $v.inputs.pk2)
                                "
                                @input="$v.inputs.pk2.$touch()"
                                @blur="$v.inputs.pk2.$touch()"
                              />
                            </template>
                            <span
                              >PK2 from topographer or IOLMaster700 can be used
                              here.</span
                            >
                          </v-tooltip>
                        </v-col>
                        <v-col class="col-12 col-md-4">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-model.number="inputs.measured_post_cyl"
                                label="Post. Cylinder (D)"
                                outlined
                                class="iol-v-text-field"
                                type="number"
                                step="0.1"
                                v-on="on"
                                :error-messages="
                                  getErrors(
                                    'measured_post_cyl',
                                    $v.inputs.measured_post_cyl,
                                  )
                                "
                                @input="$v.inputs.measured_post_cyl.$touch()"
                                @blur="$v.inputs.measured_post_cyl.$touch()"
                              />
                            </template>
                            <span
                              >Only negative values, associated with steep
                              posterior axis measurement.</span
                            >
                          </v-tooltip>
                        </v-col>
                      </v-row>
                      <v-divider cols="12" /><br />
                      <v-row>
                        <v-col class="col-12 col-md-3">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-model.number="inputs.measured_post_steep_axis"
                                label="PR2/PK2 axis (°)"
                                outlined
                                class="iol-v-text-field"
                                type="number"
                                step="1"
                                v-on="on"
                                :error-messages="
                                  getErrors(
                                    'measured_post_steep_axis',
                                    $v.inputs.measured_post_steep_axis,
                                  )
                                "
                                @input="
                                  $v.inputs.measured_post_steep_axis.$touch()
                                "
                                @blur="
                                  $v.inputs.measured_post_steep_axis.$touch()
                                "
                              />
                            </template>
                            <span
                              >PR2 axis, PK2 axis, or negative cylinder
                              axis.</span
                            >
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-sheet>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                @click="submit"
                color="#3B4256"
                class="white--text"
                x-large
              >
                Calculate
              </v-btn>
            </v-col>
          </v-row>
          <br />
          <v-row>
            <v-col cols="12" class="text-center">
              <a @click="clear" :style="$parent.resetstyle">Reset</a>
            </v-col>
          </v-row>
        </v-container>
      </form>
      <v-snackbar v-model="errorSnackbar.show" :color="errorSnackbar.color" top>
        {{ errorSnackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="errorSnackbar.show = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<style>
.v-application .primary--text {
  color: #3b4256 !important;
  caret-color: #3b4256 !important;
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import {
  integer,
  required,
  requiredIf,
  requiredUnless,
  minValue,
  between,
} from 'vuelidate/lib/validators'
import { solve_toric } from '@/graphql/queries/solve_toric.gql'
import Logout from '@/vue/Logout.vue'
import IolResultHeader from '@/components/results/IolResultHeader.vue'
import IolResultUsedParam from '@/components/results/IolResultUsedParam.vue'
import IolResultCornRefSurg from '@/components/results/IolResultCornRefSurg.vue'
import IolSelectedPowerRefract from '@/components/results/IolSelectedPowerRefract.vue'
import IolSwitchField from '@/components/elementary/IolSwitchField.vue'
import IolResultSwitchHistory from '@/components/results/IolResultSwitchHistory.vue'

let omitNull = (obj) => {
  let copy = { ...obj }
  Object.keys(copy)
    .filter((k) => copy[k] === null)
    .forEach((k) => delete copy[k])
  return copy
}

export default {
  mixins: [validationMixin],
  components: {
    Logout,
    IolResultHeader,
    IolResultUsedParam,
    IolResultCornRefSurg,
    IolSwitchField,
    IolSelectedPowerRefract,
    IolResultSwitchHistory,
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('updateToricCalcAllowed', false)
    next()
  },
  validations() {
    return {
      inputs: {
        k1_axis: { integer, required, between: between(0, 180) },
        sia: { required, between: between(0, 2) },
        sia_axis: { required, between: between(0, 359) },
        pr1: {
          required: requiredIf(function(form) {
            return this.inputs.pr2
          }),
          minValue: minValue(this.inputs.pr2),
          between: between(3, 999),
        },
        pr2: {
          required: requiredIf(function(form) {
            return this.inputs.pr1
          }),
          between: between(3, 999),
        },
        pk1: {
          required: requiredIf(function(form) {
            return this.inputs.pk2
          }),
          minValue: minValue(this.inputs.pk2),
          between: between(-20, 0),
        },
        pk2: {
          required: requiredIf(function(form) {
            return this.inputs.pk1
          }),
          between: between(-20, 0),
        },
        measured_post_cyl: {
          between: between(-20, 0),
        },
        measured_post_steep_axis: {
          required: requiredIf(function(form) {
            return (
              (this.inputs.pr1 && this.inputs.pr2) ||
              (this.inputs.pk1 && this.inputs.pk2) ||
              this.inputs.measured_post_cyl
            )
          }),
          between: between(0, 180),
        },
      },
    }
  },
  mounted() {
    setTimeout(() => {
      this.loading = false
    }, 2000)

    this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs_toric) }

    // init patient/kera/biom
    this.results_header.patientID.text = this.ginputs.patientID
    this.results_header.keratometric_index.text = this.ginputs.keratometric_index
    this.results_header.biometer.text = this.ginputs.biometer.text
    this.results_header.title = `${this.results_header.title} (from ${
      this.glastCalc.split('_')[0]
    })`

    // init common inputs
    this.common_inputs.al = this.ginputs.al
    this.common_inputs.k1 = this.ginputs.k1
    this.common_inputs.k2 = this.ginputs.k2
    this.common_inputs.acd = this.ginputs.acd
    this.common_inputs.aconst = this.ginputs.aconst
    this.common_inputs.target = this.ginputs.target
    this.common_inputs.lt = this.ginputs.lt
    this.common_inputs.cct = this.ginputs.cct
    this.common_inputs.inputs_type = this.glastCalc
    this.side = this.ginputs.side
    if (this.glastCalc === 'regular_os') {
      this.common_inputs.al = this.ginputs2.al
      this.common_inputs.k1 = this.ginputs2.k1
      this.common_inputs.k2 = this.ginputs2.k2
      this.common_inputs.acd = this.ginputs2.acd
      this.common_inputs.aconst = this.ginputs2.aconst
      this.common_inputs.target = this.ginputs2.target
      this.common_inputs.lt = this.ginputs2.lt
      this.common_inputs.cct = this.ginputs2.cct
      this.side = this.ginputs2.side
    } else if (this.glastCalc === 'complex') {
      if (this.ginputs.prc_topo) {
        this.common_inputs.total_corneal_power_measurement = this.ginputs.prc_topo
        this.common_inputs.total_corneal_power_measurement_title = 'PRC_topo'
        this.common_inputs.total_corneal_power_measurement_unit = 'mm'
      } else if (this.ginputs.tk) {
        this.common_inputs.total_corneal_power_measurement = this.ginputs.tk
        this.common_inputs.total_corneal_power_measurement_title = 'TK'
        this.common_inputs.total_corneal_power_measurement_unit = 'D'
      } else if (this.ginputs.tcrp) {
        this.common_inputs.total_corneal_power_measurement = this.ginputs.tcrp
        this.common_inputs.total_corneal_power_measurement_title = 'TCRP'
        this.common_inputs.total_corneal_power_measurement_unit = 'D'
      }
    } else if (this.glastCalc === 'second') {
      // init common inputs controlateral
      this.common_inputs_cont.al = this.ginputs.al2
      this.common_inputs_cont.k1 = this.ginputs.k12
      this.common_inputs_cont.k2 = this.ginputs.k22
      this.common_inputs_cont.acd = this.ginputs.acd2
      this.common_inputs_cont.aconst = this.ginputs.aconst
      this.common_inputs_cont.lt = this.ginputs.lt2
      this.common_inputs_cont.cct = this.ginputs.cct2
      this.common_inputs_cont.se2 = this.ginputs.se2
      this.common_inputs_cont.power2 = this.ginputs.power2
    }

    // init selected power/refractation
    this.selected_power_refract.power = this.ginputs_toric.selected_power
    this.selected_power_refract.es = this.ginputs_toric.selected_es

    // init icl/rk/non_physio_cornea
    this.switch_hist.is_icl = this.ginputs.is_icl
    this.switch_hist.is_rk = this.ginputs.is_rk
    this.switch_hist.is_non_physio_cornea = this.ginputs.is_non_physio_cornea

    // init corneal history
    if (['complex', 'second'].includes(this.glastCalc)) {
      this.corneal_history.is_corneal_laser = this.ginputs.is_corneal_laser
      this.corneal_history.corneal_laser_type = this.ginputs.corneal_laser_type.text
      this.corneal_history.pre_lvc_arc = this.ginputs.pre_lvc_arc
      this.corneal_history.laser_amount = this.ginputs.laser_amount
    }
  },
  data: () => {
    let ret = {
      pr12pk12cylColor: 'black',
      results_header: {
        title: 'PEARL-DGS - Toric Calculation',
        patientID: {
          label: 'Patient ID / Name',
          readonly: true,
          tooltip: '',
          text: '',
        },
        keratometric_index: {
          label: 'Keratometric Index',
          readonly: true,
          tooltip: '',
          text: '',
        },
        biometer: {
          label: 'biometer',
          readonly: true,
          tooltip: '',
          text: '',
        },
      },
      side: false,
      switch_hist: {
        is_rk: false,
        is_icl: false,
        is_non_physio_cornea: false,
      },
      common_inputs: {
        al: '',
        k1: '',
        k2: '',
        acd: '',
        aconst_title: 'A Constant',
        aconst: '',
        target_title: 'Target refraction',
        target: '',
        lt: '',
        cct: '',
        total_corneal_power_measurement_title: '',
        total_corneal_power_measurement_unit: '',
        total_corneal_power_measurement: '',
      },
      common_inputs_cont: {
        al: '',
        k1: '',
        k2: '',
        acd: '',
        aconst_title: 'Implanted A constant',
        aconst: '',
        target_title: 'Target refraction',
        target: '',
        lt: '',
        cct: '',
        se2_title: 'Postop. spherical equivalent',
        se2: '',
        power2_title: 'Implanted IOL power',
        power2: '',
        inputs_type: 'controlateral',
        is_outlined: true,
      },
      corneal_history: {
        is_corneal_laser: false,
        corneal_laser_type: '',
        pre_lvc_arc: '',
        laser_amount: '',
      },
      inputs: {
        k1_axis: '',
        sia: '',
        sia_axis: '',
        style: { type: 'Posterior', text: 'Posterior' },
        notation: { type: 'minus', text: 'Minus' },
        pr1: null,
        pr2: null,
        pk1: null,
        pk2: null,
        measured_post_cyl: null,
        measured_post_steep_axis: null,
      },
      inputs_default: {
        k1_axis: '',
        sia: '',
        sia_axis: '',
        style: { type: 'Posterior', text: 'Posterior' },
        notation: { type: 'minus', text: 'Minus' },
        pr1: null,
        pr2: null,
        pk1: null,
        pk2: null,
        measured_post_cyl: null,
        measured_post_steep_axis: null,
      },
      selected_power_refract: {
        th1: 'IOL POWER (D)',
        th2: 'REFRACTION (D)',
        power: '',
        es: '',
      },
      errorSnackbar: {
        show: false,
        text: '',
        color: 'error',
      },
    }
    if (process.env.NODE_ENV == 'development') {
      ret.inputs = {
        ...ret.inputs,
        k1_axis: 3,
        sia: 1.2,
        sia_axis: 7,
        style: { type: 'Posterior', text: 'Posterior' },
        notation: { type: 'minus', text: 'Minus' },
        pr1: 6,
        pr2: 4,
        pk1: null,
        pk2: null,
        measured_post_cyl: null,
        measured_post_steep_axis: 2,
      }
    }
    return ret
  },

  computed: {
    ...mapState([
      'ginputs',
      'ginputs2',
      'ginputs_toric',
      'appHeader',
      'gtoric_calc_allowed',
      'glastCalc',
    ]),
  },

  methods: {
    getErrors(name, model) {
      const errors = []
      if (!model || !model.hasOwnProperty('$dirty')) return errors
      if (!model.$dirty) return errors
      switch (name) {
        case 'k1_axis':
        case 'sia':
        case 'sia_axis':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'measured_post_steep_axis':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'pr1':
          this.inputs.pr2 &&
            !model.minValue &&
            errors.push('PR1 must be greater than PR2')
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'pr2':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'pk1':
          this.inputs.pk2 &&
            !model.minValue &&
            errors.push('PK1 must be greater than PK2')
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'pk2':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'measured_post_cyl':
          !model.between && errors.push('value out of bounds')
          break
        default:
          break
      }
      return errors
    },
    isNotPr12Pk12CylExlusiv() {
      return (
        ((this.inputs.pr1 || this.inputs.pr2) &&
          (this.inputs.pk1 || this.inputs.pk2)) ||
        ((this.inputs.pr1 || this.inputs.pr2) &&
          this.inputs.measured_post_cyl) ||
        ((this.inputs.pk1 || this.inputs.pk2) && this.inputs.measured_post_cyl)
      )
    },
    focusFirstError(component = this) {
      if (component.hasError) {
        component.focus()
        return true
      }

      let focused = false

      component.$children.some((childComponent) => {
        focused = this.focusFirstError(childComponent)
        return focused
      })

      return focused
    },
    filterUsefulGinputsForToric() {
      const suitableCommonInputs = [
        'side',
        'keratometric_index',
        'biometer',
        'iol_model',
        'aconst',
        'target',
        'al',
        'k1',
        'k2',
        'acd',
        'lt',
        'cct',
      ]
      const suitableComplexInputs = [
        'is_icl',
        'is_rk',
        'is_non_physio_cornea',
        'is_corneal_laser',
        'corneal_laser_type',
        'pre_lvc_arc',
        'laser_amount',
        'prc_topo',
        'tk',
        'tcrp',
      ]
      const suitableSecondInputs = [
        'is_second',
        'is_icl',
        'is_corneal_laser',
        'corneal_laser_type',
        'pre_lvc_arc',
        'laser_amount',
        'al2',
        'k12',
        'k22',
        'acd2',
        'lt2',
        'cct2',
        'pre_lvc_arc2',
        'laser_amount2',
        'power2',
        'se2',
      ]
      let ginputs_filtered = { ...omitNull(this.ginputs) }
      if (this.glastCalc === 'regular_os') {
        ginputs_filtered = { ...omitNull(this.ginputs2) }
      }
      let inputsFilter = suitableCommonInputs
      if (this.glastCalc === 'complex') {
        inputsFilter = inputsFilter.concat(suitableComplexInputs)
      } else if (this.glastCalc === 'second') {
        inputsFilter = inputsFilter.concat(suitableSecondInputs)
      }
      Object.keys(ginputs_filtered)
        .filter((k) => !inputsFilter.includes(k))
        .forEach((k) => delete ginputs_filtered[k])

      // do not send corneal history if is_corneal false
      if (ginputs_filtered['is_corneal_laser'] === false) {
        delete ginputs_filtered['corneal_laser_type']
        delete ginputs_filtered['laser_amount']
        delete ginputs_filtered['pre_lvc_arc']
        delete ginputs_filtered['laser_amount2']
        delete ginputs_filtered['pre_lvc_arc2']
      }

      return ginputs_filtered
    },
    submit() {
      this.$v.inputs.$touch()
      this.$nextTick(() => {
        if (this.isNotPr12Pk12CylExlusiv()) {
          this.pr12pk12cylColor = 'red'
          return
        } else {
          this.pr12pk12cylColor = 'black'
        }
        if (this.focusFirstError()) {
          return
        }
        Object.keys(this.inputs).forEach(
          (k) =>
            (this.inputs[k] = this.inputs[k] === '' ? null : this.inputs[k]),
        )

        // update store
        const inputs = {
          ...omitNull(this.inputs),
          ...this.filterUsefulGinputsForToric(),
        }
        this.$store.dispatch('updateInputsToric', this.inputs)

        // delete those inputs because not used in calculation, so not sent via graphql,
        // only stored in ginputs for reuse on other tab
        delete inputs['patientID']
        delete inputs['selected_es'] // only power used in calc

        // purge select inputs
        const select_inputs = [
          'iol_model',
          'biometer',
          'corneal_laser_type',
          'style',
          'notation',
        ]
        for (const select_input of select_inputs) {
          if (select_input in inputs) {
            inputs[select_input] = inputs[select_input].type
          }
        }

        this.$apollo
          .query({
            query: solve_toric,
            variables: {
              inputs,
            },
          })
          .then((result) => {
            this.$store.dispatch('setResultsToric', result.data.solve_toric)
            this.$router.push('result_toric')
          })
          .catch((e) => {
            this.$raven.captureException(e)
            this.errorSnackbar.text =
              e.message || 'An error occurred during calculation'
            this.errorSnackbar.show = true
          })
      })
    },
    clear() {
      Object.keys(this.inputs).forEach(
        (k) =>
          (this.inputs[k] =
            this.inputs_default[k] !== null ? this.inputs_default[k] : null),
      )
      // Reset store
      this.$store.dispatch('resetInputsToric')
      this.$refs.first.focus()
    },
    ...mapActions(['setResultsToric', 'updateInputsToric', 'setAuthUser', 'resetInputsToric']),
  },
}
</script>
